/**
 * Test Results Hooks
 *
 * React Query hooks for medical test result CRUD operations.
 */

import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";
import { throwApiError } from "@src/packages/api/api-error";

// =============================================================================
// Types
// =============================================================================

type TestResultsResponse = ResponseBody<
  paths["/{tenantId}/patients/{patientId}/test-results"]["get"]
>;

/** A test result returned by the API, including its source and verification metadata. */
export type TestResult = TestResultsResponse["data"][number];

export type TestResultType = NonNullable<TestResult["type"]>;
export type BloodTestCategory = NonNullable<TestResult["bloodTestCategory"]>;
export type GeneticsTestCategory = NonNullable<
  TestResult["geneticsTestCategory"]
>;
export type ImagingTestCategory = NonNullable<
  TestResult["imagingTestCategory"]
>;
export type SemenFluidTestCategory = NonNullable<
  TestResult["semenFluidTestCategory"]
>;

export type CreateTestResultPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}/test-results"]["post"]
>;

export type UpdateTestResultPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}/test-results/{testResultId}"]["patch"]
>;

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to get all test results for a patient (defaults to the current user).
 * Pass `opts.patientId` to fetch a different patient's results (e.g. a linked partner).
 */
export function useTestResults(opts?: {
  patientId?: string | null;
  /** See `useDocuments` — lets a closed surface skip the round-trip. */
  enabled?: boolean;
}) {
  const { client, tenantId, patientId: currentPatientId } = useApiClient();
  const patientId = opts?.patientId ?? currentPatientId;

  return useQuery<TestResult[]>({
    queryKey: ["test-results", tenantId, patientId],
    queryFn: async () => {
      const { data, error } = await client.GET(
        "/{tenantId}/patients/{patientId}/test-results",
        {
          params: {
            path: {
              tenantId: tenantId!,
              patientId: patientId!,
            },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(error, "Failed to load test results", "test-results");
      }
    },
    enabled: (opts?.enabled ?? true) && !!patientId && !!tenantId,
  });
}

/**
 * Hook to create a new test result
 */
export function useCreateTestResult() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = ["test-results", tenantId, patientId];

  return useMutation({
    mutationFn: async (payload: CreateTestResultPayload) => {
      if (!tenantId || !patientId) {
        throw new Error(
          "Cannot create test result: missing tenantId or patientId",
        );
      }

      const { data, error } = await client.POST(
        "/{tenantId}/patients/{patientId}/test-results",
        {
          params: {
            path: {
              tenantId,
              patientId,
            },
          },
          body: payload,
        },
      );

      if (data) {
        return data;
      } else {
        throwApiError(error, "Failed to create test result", "test-results");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    },
  });
}

/**
 * Hook to update an existing test result
 */
export function useUpdateTestResult() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = ["test-results", tenantId, patientId];

  return useMutation({
    mutationFn: async ({
      testResultId,
      data: payload,
    }: {
      testResultId: string;
      data: UpdateTestResultPayload;
    }) => {
      if (!tenantId || !patientId) {
        throw new Error(
          "Cannot update test result: missing tenantId or patientId",
        );
      }

      const { data, error } = await client.PATCH(
        "/{tenantId}/patients/{patientId}/test-results/{testResultId}",
        {
          params: {
            path: {
              tenantId,
              patientId,
              testResultId,
            },
          },
          body: payload,
        },
      );

      if (data) {
        return data;
      } else {
        throwApiError(error, "Failed to update test result", "test-results");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Hook to delete a test result
 */
export function useDeleteTestResult() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = ["test-results", tenantId, patientId];

  return useMutation({
    mutationFn: async (testResultId: string) => {
      if (!tenantId || !patientId) {
        throw new Error(
          "Cannot delete test result: missing tenantId or patientId",
        );
      }

      const { error } = await client.DELETE(
        "/{tenantId}/patients/{patientId}/test-results/{testResultId}",
        {
          params: {
            path: {
              tenantId,
              patientId,
              testResultId,
            },
          },
        },
      );

      if (error) {
        throwApiError(error, "Failed to delete test result", "test-results");
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Hook to create multiple test results (for Blood/Hormones with multiple markers)
 */
export function useCreateTestResults() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = ["test-results", tenantId, patientId];

  // Signatures of rows already persisted in an earlier attempt of the same
  // save. If some rows succeed and others fail, the caller keeps the modal open
  // and the user retries — without this, the retry would re-POST the rows that
  // already saved and create duplicates. Scoped to this hook instance, so it
  // resets when the screen unmounts.
  const createdSignatures = useRef<Set<string>>(new Set());

  return useMutation({
    mutationFn: async (payloads: CreateTestResultPayload[]) => {
      if (!tenantId || !patientId) {
        throw new Error(
          "Cannot create test results: missing tenantId or patientId",
        );
      }

      const signatureOf = (payload: CreateTestResultPayload) =>
        JSON.stringify(payload);

      // Skip rows already created in a prior (partially-failed) attempt.
      const pending = payloads.filter(
        (payload) => !createdSignatures.current.has(signatureOf(payload)),
      );

      const outcomes = await Promise.allSettled(
        pending.map(async (payload) => {
          const result = await client.POST(
            "/{tenantId}/patients/{patientId}/test-results",
            {
              params: {
                path: {
                  tenantId,
                  patientId,
                },
              },
              body: payload,
            },
          );
          if (result.error) {
            throw new Error("Failed to create test result");
          }
          return { payload, row: result.data?.data };
        }),
      );

      const succeeded = outcomes.filter((o) => o.status === "fulfilled");
      // Remember what saved so a retry only re-sends the rows that failed.
      succeeded.forEach((o) =>
        createdSignatures.current.add(signatureOf(o.value.payload)),
      );

      const failureCount = outcomes.length - succeeded.length;
      if (failureCount > 0) {
        throw new Error(`Failed to create ${failureCount} test results`);
      }

      // All rows persisted — reset so an unrelated later save starts clean.
      createdSignatures.current.clear();
      return { data: succeeded.map((o) => o.value.row).filter(Boolean) };
    },
    // Invalidate even on partial failure so the rows that did save show up.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    },
  });
}
